import { arrayOf, func, number, oneOfType, shape, string } from "prop-types";
import {useTranslation} from "react-i18next";
import {Controller, useForm} from "react-hook-form";
import * as yup from "yup";
import {yupResolver} from "@hookform/resolvers/yup";
import {Autocomplete, Button, ControlledTextField} from "@components";
import {CircularProgress, Divider, Grid} from "@material-ui/core";

const SCOPES = ["mcp:read", "mcp:write", "mcp:draft", "mcp:full"];

const apiKeysFormSchema = t =>
  yup.object().shape({
    userId: yup.string().required(t("errors.api_key.user.required")),
    name: yup.string().max(250, t("errors.max")).nullable(true),
    scope: yup.string().required(t("errors.api_key.scope.required")),
  });

export const ApiKeysForm = ({
  onSubmit,
  onCancel,
  members
}) => {
  const { t } = useTranslation();

  const {
    control,
    handleSubmit,
    formState: {isSubmitting},
  } = useForm({
    resolver: yupResolver(apiKeysFormSchema(t)),
    defaultValues: {userId: "", name: "", scope: ""},
  });

  const submitHandler = async data => {
    await onSubmit({
      userId: Number(data.userId),
      name: data.name.trim() || null,
      scope: data.scope,
    });
  };

  return (
    <form
      autoComplete="on"
      aria-label={t("views.settings.api_keys.form")}
      onSubmit={handleSubmit(submitHandler)}
    >
      <Grid item container direction="column">
        <Controller
          name="userId"
          control={control}
          render={({ field: {onChange, value, name}, fieldState: {error} }) => (
            <Autocomplete
              value={value}
              options={members}
              name={name}
              labelKey="fullname"
              idKey="id"
              inputLabel={t("views.settings.api_keys.user")}
              noOptionsText={t("common.no_options")}
              fieldClassName="mb-4"
              onChange={onChange}
              disableClearable
              error={Boolean(error)}
              helperText={error?.message}
            />
          )}
        />
        <ControlledTextField
          name="name"
          control={control}
          label={t("views.settings.api_keys.name")}
          fullWidth
          inputProps={{maxLength: 250}}
          className="mb-4"
        />
        <Controller
          name="scope"
          control={control}
          render={({ field: {onChange, value, name}, fieldState: {error} }) => (
            <Autocomplete
              value={value}
              options={SCOPES.map(scope => ({id: scope, name: scope}))}
              name={name}
              idKey="id"
              labelKey="name"
              inputLabel={t("views.settings.api_keys.scope")}
              noOptionsText={t("common.no_options")}
              fieldClassName="mb-4"
              onChange={onChange}
              disableClearable
              error={Boolean(error)}
              helperText={error?.message}
            />
          )}
        />
        
        <Divider className="mt-2 -mx-6 mb-6" />

        <Grid item container justifyContent="flex-end">
          <Button onClick={onCancel} className="mr-2">
            {t("common.cancel")}
          </Button>
          <Button
            isSubmit
            name="submit"
            variant="contained"
            color="primary"
            endIcon={
              isSubmitting ? (
                <CircularProgress
                  aria-label="progress indicator"
                  size={20}
                  color="inherit"
                />
              ) : undefined
            }
          >
            {t("views.settings.api_keys.create")}
          </Button>
        </Grid>
      </Grid>
    </form>
  );
};

ApiKeysForm.propTypes = {
  onSubmit: func.isRequired,
  onCancel: func.isRequired,
  members: arrayOf(shape({
    id: oneOfType([number, string]).isRequired,
    fullname: string,
  })).isRequired,
};
